/**
 * Service de tracking pour la landing page
 * Enregistre les événements (page views, clics) avec IP, pays, etc.
 */

import { getDeviceInfo } from '../utils/deviceDetection';

export enum EventType {
  PAGE_VIEW = 'PAGE_VIEW',
  CLICK = 'CLICK',
  SCROLL = 'SCROLL',
}

interface TrackingEvent {
  eventType: EventType;
  path: string;
  elementId?: string;
  elementType?: string;
  elementText?: string;
  url?: string;
  sessionId?: string;
  userAgent?: string;
  referer?: string;
  // Informations device
  deviceType?: string;
  browserName?: string;
  browserVersion?: string;
  os?: string;
  osVersion?: string;
  locale?: string;
  language?: string;
  timezone?: string;
  screenWidth?: number;
  screenHeight?: number;
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
}

class TrackingService {
  private sessionId: string;
  private apiUrl: string | null = null;

  constructor() {
    // Générer un ID de session unique
    this.sessionId = this.generateSessionId();
    
    // Détecter l'URL de l'API
    this.detectApiUrl();
  }

  private generateSessionId(): string {
    // Récupérer depuis le sessionStorage si existe, sinon créer un nouveau
    const stored = sessionStorage.getItem('tracking_session_id');
    if (stored) {
      return stored;
    }
    
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', newId);
    return newId;
  }

  private detectApiUrl(): void {
    // Utiliser la même logique que ContactPage
    const rawApiUrl = import.meta.env.VITE_API_BASE_URL;
    let apiUrl: string;
    
    if (rawApiUrl && rawApiUrl.trim() !== '') {
      apiUrl = rawApiUrl.trim();
      apiUrl = apiUrl.replace(/http\/+/g, 'http://');
      apiUrl = apiUrl.replace(/https\/+/g, 'https://');
      
      if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
        apiUrl = '';
      } else {
        apiUrl = apiUrl.replace(/\/+$/, '');
      }
    } else {
      apiUrl = '';
    }
    
    if (!apiUrl || apiUrl === '') {
      const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
      const hostname = window.location.hostname;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        apiUrl = 'http://localhost:3000/api/v1';
      } else {
        apiUrl = `${protocol}://${hostname}:3000/api/v1`;
      }
    }
    
    this.apiUrl = apiUrl;
  }

  private async sendEvent(event: TrackingEvent): Promise<void> {
    if (!this.apiUrl) {
      this.detectApiUrl();
    }

    try {
      const endpoint = `${this.apiUrl}/tracking/event`;
      
      // Récupérer les informations du device
      const deviceInfo = getDeviceInfo();
      
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...event,
          sessionId: this.sessionId,
          userAgent: navigator.userAgent,
          referer: document.referrer || undefined,
          url: window.location.href,
          // Ajouter les informations du device
          ...deviceInfo,
        }),
      });
    } catch (error) {
      // Ne pas faire échouer l'application si le tracking échoue
      console.debug('Tracking error (silent):', error);
    }
  }

  /**
   * Tracker une vue de page
   */
  trackPageView(path: string): void {
    this.sendEvent({
      eventType: EventType.PAGE_VIEW,
      path,
    });
  }

  /**
   * Tracker un clic sur un élément
   */
  trackClick(
    element: HTMLElement | EventTarget | null,
    path?: string
  ): void {
    if (!element) return;

    const target = element instanceof HTMLElement ? element : null;
    if (!target) return;

    const elementId = target.id || undefined;
    const elementType = target.tagName.toLowerCase();
    const elementText = this.getElementText(target);

    this.sendEvent({
      eventType: EventType.CLICK,
      path: path || window.location.pathname,
      elementId,
      elementType,
      elementText,
    });
  }

  /**
   * Extraire le texte d'un élément (pour les boutons, liens, etc.)
   */
  private getElementText(element: HTMLElement): string | undefined {
    // Essayer de récupérer le texte visible
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 200) {
      return text;
    }

    // Sinon, essayer les attributs communs
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    const title = element.getAttribute('title');
    if (title) return title;

    const alt = element.getAttribute('alt');
    if (alt) return alt;

    return undefined;
  }

  /**
   * Initialiser le tracking automatique des clics
   */
  initClickTracking(): void {
    // Écouter tous les clics sur le document
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        // Ignorer certains éléments (scripts, styles, etc.)
        if (['script', 'style', 'meta', 'link'].includes(target.tagName.toLowerCase())) {
          return;
        }
        
        this.trackClick(target);
      }
    }, true); // Utiliser capture pour attraper tous les clics
  }
}

// Instance singleton
export const trackingService = new TrackingService();

